import {
   FaPlusCircle,
   FaMinusCircle,
   FaBoxOpen,
   FaBoxes,
   FaSearchengin,
   FaPaste,
   FaTachometerAlt,
   FaFileInvoiceDollar,
   FaUserTie,
   FaIdCard,
   FaListAlt,
   FaMapMarkedAlt,
   FaClipboardList,
   FaChartBar,
   FaUsers,
   FaBriefcase,
   FaUniversity,
   FaKey,
   FaCommentsDollar,
   FaChartLine,
   FaExchangeAlt,
   FaTruck,
   FaBalanceScale,
} from 'react-icons/fa';

/**
 * @typedef {Object} NavChild
 * @property {string} to - Rota do react-router
 * @property {string} label - Texto exibido no menu
 * @property {React.ComponentType} icon - Componente de ícone
 * @property {string[]} permissionAny - Requer QUALQUER uma dessas permissões (array vazio = sem restrição)
 * @property {boolean} [newItem] - Se true, exibe badge "Novo!" à direita do item
 */

/**
 * @typedef {Object} NavGroup
 * @property {string} key - Identificador único do grupo
 * @property {string} label
 * @property {React.ComponentType} icon
 * @property {NavChild[]} children
 */

/**
 * @typedef {Object} NavTopItem
 * @property {string} to
 * @property {string} title
 * @property {React.ComponentType} icon
 * @property {string} label
 * @property {string} permission - Permissão única obrigatória
 */

/**
 * @typedef {Object} ModuleNav
 * @property {NavTopItem[]} topItems - Itens diretos sem submenu (aparecem no topo)
 * @property {NavGroup[]} groups - Grupos com submenus
 */

/**
 * Catálogo de navegação por módulo.
 * Para adicionar um novo módulo, basta registrar aqui (e em MODULE_CONFIG do ModuleContext).
 * @type {Object.<string, ModuleNav>}
 */
const MODULES_NAV = {
   estoque: {
      topItems: [
         // A home (/main) SAIU daqui em 2026-08-21: virou entrada fixa da
         // sidebar (HOME_ITEM em MenuItems.jsx), sem RBAC e fora do módulo.
         // Enquanto era topItem de `estoque` com permission
         // `inventory:stock:read`, quem não tinha essa chave não enxergava
         // nenhum caminho de volta pra home.
         {
            to: '/advancedstocksearch',
            title: 'Pesquisa avançada de estoque',
            icon: FaPaste,
            label: 'Inventário',
            permission: 'inventory:advanced_stock:search',
         },
      ],
      groups: [
         {
            key: 'movimentos',
            label: 'Movimentos',
            icon: FaPaste,
            children: [
               {
                  to: '/movement-in',
                  label: 'Entrada de produtos',
                  icon: FaPlusCircle,
                  permissionAny: ['inventory:movement:create'],
               },
               {
                  to: '/movement-out',
                  label: 'Saída de produtos',
                  icon: FaMinusCircle,
                  permissionAny: ['inventory:movement:create'],
               },
               {
                  to: '/movements',
                  label: 'Consultar movimentos',
                  icon: FaPaste,
                  permissionAny: ['inventory:movement:read'],
               },
               {
                  to: '/movement-in-by-xml',
                  label: 'Criar movimento via XML',
                  icon: FaPaste,
                  permissionAny: ['inventory:movement:create'],
               },
            ],
         },
         {
            key: 'produtos',
            label: 'Produtos',
            icon: FaBoxes,
            children: [
               {
                  to: '/products',
                  label: 'Lista de produtos',
                  icon: FaBoxes,
                  permissionAny: ['inventory:product:read'],
               },
               {
                  to: '/product',
                  label: 'Novo produto',
                  icon: FaBoxOpen,
                  permissionAny: ['inventory:product:create'],
               },
               {
                  to: '/prodbrands',
                  label: 'Gerenciar marcas',
                  icon: FaBoxOpen,
                  permissionAny: [],
               },
               {
                  to: '/categories',
                  label: 'Gerenciar categorias',
                  icon: FaBoxOpen,
                  permissionAny: ['inventory:category:read'],
               },
               {
                  to: '/product-query',
                  label: 'Pesquisar estoque disponível',
                  icon: FaSearchengin,
                  permissionAny: ['inventory:stock:read'],
               },
            ],
         },
         {
            // Transferência entre PDVs (handoff 0099, Fase 1 PUSH). Duas
            // entradas de fila por público: quem EXPEDE trabalha na ordem
            // (origem), quem CONFERE trabalha na remessa (destino).
            key: 'transferencias',
            label: 'Transferências',
            icon: FaExchangeAlt,
            children: [
               {
                  to: '/inventory/transfers',
                  label: 'Transferências entre PDVs',
                  icon: FaExchangeAlt,
                  permissionAny: ['inventory:transfer:read'],
                  newItem: true,
               },
               {
                  to: '/inventory/transfers/new',
                  label: 'Nova transferência',
                  icon: FaPlusCircle,
                  permissionAny: ['inventory:transfer:dispatch'],
                  newItem: true,
               },
               {
                  to: '/inventory/shipments',
                  label: 'Remessas em trânsito',
                  icon: FaTruck,
                  permissionAny: ['inventory:transfer:read'],
                  newItem: true,
               },
               {
                  to: '/inventory/stock-position',
                  label: 'Posição de estoque',
                  icon: FaBalanceScale,
                  permissionAny: ['inventory:transfer:read'],
                  newItem: true,
               },
            ],
         },
         {
            key: 'snapshots',
            label: 'Snapshots',
            icon: FaSearchengin,
            children: [
               {
                  to: '/stock/history-by-date',
                  label: 'Histórico por data',
                  icon: FaSearchengin,
                  permissionAny: [],
               },
               {
                  to: '/stock/evolution',
                  label: 'Evolução de estoque',
                  icon: FaSearchengin,
                  permissionAny: [],
               },
               {
                  to: '/stock/low-stock-products',
                  label: 'Produtos com baixo estoque',
                  icon: FaSearchengin,
                  permissionAny: [],
               },
               {
                  to: '/stock/pdv-snapshot',
                  label: 'Snapshot PDV',
                  icon: FaSearchengin,
                  permissionAny: [],
               },
               {
                  to: '/stock/comparison',
                  label: 'Comparação de estoque',
                  icon: FaSearchengin,
                  permissionAny: [],
               },
            ],
         },
      ],
   },

   financeiro: {
      topItems: [],
      groups: [
         {
            // Emissão/consulta de boletos — separado da gestão de cobrança
            // (reorganização de 2026-08-14).
            key: 'boletos',
            label: 'Boletos',
            icon: FaFileInvoiceDollar,
            children: [
               {
                  to: '/billing',
                  label: 'Listar boletos',
                  icon: FaFileInvoiceDollar,
                  permissionAny: ['finance:charge:read'],
               },
               {
                  to: '/billing/new',
                  label: 'Novo boleto',
                  icon: FaPlusCircle,
                  permissionAny: ['finance:charge:create'],
               },
            ],
         },
         {
            // Régua de cobrança (handoff 0090) + visões gerenciais.
            key: 'gestao-cobrancas',
            label: 'Gestão de cobranças',
            icon: FaCommentsDollar,
            children: [
               {
                  // Kanban operacional de casos vencidos, um caso por cliente.
                  to: '/finance/dunning',
                  label: 'Central de cobranças',
                  icon: FaCommentsDollar,
                  permissionAny: ['finance:dunning:read'],
                  newItem: true,
               },
               {
                  // Visão gerencial da carga de acompanhamento (adendo 01) —
                  // tela própria, sem os filtros operacionais da Central.
                  to: '/finance/dunning/timeline',
                  label: 'Timeline cobranças',
                  icon: FaChartLine,
                  permissionAny: ['finance:dunning:read'],
                  newItem: true,
               },
               {
                  // Acerto com o escritório de cobrança (handoff 0103): a linha
                  // é o TÍTULO, não o caso. Chave PRÓPRIA — quem opera a régua
                  // não vê esta tela, e é assim de propósito (§7).
                  to: '/finance/dunning/reconciliation',
                  label: 'Relatório cobrança externa',
                  icon: FaFileInvoiceDollar,
                  permissionAny: ['finance:dunning:reconcile'],
                  newItem: true,
               },
               {
                  // Permissão própria (`finance:dashboard:read`), a mesma que
                  // gateia a rota — quem enxerga cobranças não enxerga por
                  // consequência o painel de crediário.
                  to: '/billing/dashboard',
                  label: 'Indicadores de recebíveis',
                  icon: FaTachometerAlt,
                  permissionAny: ['finance:dashboard:read'],
               },
            ],
         },
         // Contas recebedoras + credenciais da ASAAS (handoff 0079). Gestão de
         // cadastro, não operação do dia a dia — por isso grupo próprio, fora de
         // "Cobranças". A credencial não tem entrada de menu: vive na ficha da
         // conta a que pertence.
         {
            key: 'fin-cadastros',
            label: 'Cadastros',
            icon: FaUniversity,
            children: [
               {
                  to: '/finance/bank-accounts',
                  label: 'Contas bancárias',
                  icon: FaUniversity,
                  permissionAny: ['finance:bank_account:read'],
                  newItem: true,
               },
               {
                  to: '/finance/bank-accounts/new',
                  label: 'Nova conta bancária',
                  icon: FaKey,
                  permissionAny: ['finance:bank_account:create'],
                  newItem: true,
               },
            ],
         },
         {
            key: 'fiscal',
            label: 'Controle Fiscal',
            icon: FaListAlt,
            children: [
               {
                  to: '/finance/service-aquisition',
                  label: '[CF] Serviços Tomados',
                  icon: FaListAlt,
                  permissionAny: ['finance:service_aquisition:read'],
               },
               {
                  to: '/finance/service-aquisition/providers',
                  label: 'Fornecedores de serviço',
                  icon: FaListAlt,
                  permissionAny: ['finance:service_aquisition:read'],
               },
            ],
         },
      ],
   },

   roteirização: {
      topItems: [
         {
            to: '/routing/reports/productivity',
            title: 'Dashboard',
            icon: FaChartBar,
            label: 'Dashboard',
            permission: 'routing:report:read',
         },
      ],
      groups: [
         {
            key: 'routing-agenda',
            label: 'Roteirização',
            icon: FaMapMarkedAlt,
            children: [
               {
                  // Master-detail (handoff 0036): a nav abre o ÍNDICE de
                  // roteirizações; o board (/routing/management) é alcançado ao
                  // escolher um card. Não apontar mais direto pro board.
                  to: '/routing/schedules',
                  label: 'Gestão de rotas',
                  icon: FaMapMarkedAlt,
                  permissionAny: ['routing:schedule:read'],
                  newItem: true,
               },
               {
                  to: '/routing/items',
                  label: 'Lista de serviços',
                  icon: FaClipboardList,
                  permissionAny: ['routing:schedule_item:read'],
                  newItem: true,
               },
               {
                  to: '/routing/items/new',
                  label: 'Novo item',
                  icon: FaPlusCircle,
                  permissionAny: ['routing:schedule_item:create'],
                  newItem: true,
               },
               {
                  to: '/routing/service-catalog',
                  label: 'Catálogo de serviços',
                  icon: FaListAlt,
                  // Dois donos (handoff 0039 §2): logística (cadastro/tempo) e
                  // financeiro (comissão). Ambos enxergam a entrada.
                  permissionAny: [
                     'routing:service_catalog:manage',
                     'finance:commission:manage',
                  ],
                  newItem: true,
               },
            ],
         },
      ],
   },

   comercial: {
      topItems: [],
      groups: [
         {
            key: 'clientes',
            label: 'Clientes',
            icon: FaUsers,
            children: [
               {
                  to: '/comercial/clientes',
                  label: 'Lista de clientes',
                  icon: FaIdCard,
                  permissionAny: ['customers:customer:read'],
                  newItem: true,
               },
               {
                  to: '/comercial/clientes/novo',
                  label: 'Novo cliente',
                  icon: FaPlusCircle,
                  permissionAny: ['customers:customer:create'],
                  newItem: true,
               },
            ],
         },
      ],
   },

   funcionários: {
      topItems: [],
      groups: [
         {
            key: 'funcionarios',
            label: 'Funcionários',
            icon: FaUserTie,
            children: [
               {
                  to: '/hr/employees',
                  label: 'Lista de funcionários',
                  icon: FaIdCard,
                  permissionAny: ['hr:employee:read'],
               },
               {
                  to: '/hr/employees/debts-report',
                  label: 'Relatório de débitos',
                  icon: FaIdCard,
                  permissionAny: ['hr:employee:read'],
               },
               {
                  to: '/hr/employees/new',
                  label: 'Cadastrar funcionário',
                  icon: FaPlusCircle,
                  permissionAny: ['hr:employee:create'],
               },
            ],
         },
         {
            key: 'recrutamento',
            label: 'Recrutamento & Seleção',
            icon: FaUsers,
            children: [
               {
                  to: '/hr/recruitment/pool',
                  label: 'Banco de currículos',
                  icon: FaClipboardList,
                  permissionAny: ['hr:candidate:read'],
                  newItem: true,
               },
               {
                  to: '/hr/recruitment/candidates/new',
                  label: 'Cadastrar candidato',
                  icon: FaPlusCircle,
                  permissionAny: ['hr:candidate:create'],
                  newItem: true,
               },
               {
                  to: '/hr/recruitment/openings',
                  label: 'Vagas',
                  icon: FaBriefcase,
                  permissionAny: ['hr:job_opening:read'],
                  newItem: true,
               },
               {
                  to: '/hr/recruitment/origins',
                  label: 'Origens de candidatura',
                  icon: FaBoxOpen,
                  permissionAny: [
                     'hr:candidate:read',
                     'hr:recruitment_catalog:manage',
                  ],
                  newItem: true,
               },
               {
                  to: '/hr/recruitment/position-requirements',
                  label: 'Requisitos por cargo',
                  icon: FaClipboardList,
                  permissionAny: [
                     'hr:job_position_requirement:read',
                     'hr:job_position_requirement:manage',
                  ],
                  newItem: true,
               },
            ],
         },
      ],
   },
};

export default MODULES_NAV;
